<template>
  <div class="customer-service">
    <section
      v-for="item in list"
      :key="item.name"
      class="service-item"
      @click="toApp(item)"
    >
      <div class="item-content">
        <img class="service-icon" :src="item.icon" :alt="item.name" />
        <span class="service-title">{{ $t(item.title) }}</span>
      </div>
      <img class="arrow-icon" src="../assets/left-arrows.png" alt="arrow" />
    </section>
  </div>
</template>

<script setup>
import { reactive, toRefs, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Dialog } from 'vant'
import dsBridge from 'dsbridge'
import { getImageUrl, isIOS } from '../utils/util'
import { getUrlQuery } from '../utils/getUrlQuery'

const { t, locale } = useI18n()

// 响应式状态
const state = reactive({
  list: [],
  overseasList: [
    {
      name: 'whatsApp',
      title: 'service@lavamusic.com',
      url: 'mailto:service@lavamusic.com',
      icon: require('../assets/email.png'),
      analytics: 'WhatsApp'
    }
  ],
  domesticList: [
    {
      name: 'weChat',
      title: '微信',
      url: 'https://work.weixin.qq.com/kfid/kfc9b10d4e774c9f761',
      icon: require('../assets/Wechat.png'),
      analytics: '企业微信'
    }
  ]
})

// 设置语言和客服列表
const region = getUrlQuery('region')
const lang = getUrlQuery('lang')

if (lang === 'cn') locale.value = 'cn'
state.list = region === 'zh' ? state.domesticList : state.overseasList

if (region === 'zh' && lang === 'en') {
  state.domesticList[0].title = 'WeChat'
}

// 保存图片
const saveImg = (url) => {
  dsBridge.call('saveFileWithUrl', { type: 'image', url }, (res) => {
    console.log('saveFileWithUrl', res)
  })
}

// 弹窗消息模板（改用模板字符串）
const createDialogContent = (content) => {
  return {
    template: `
      <div class="relative">
        ${content}
        <img 
          @click="close" 
          src="${getImageUrl('Union.png')}" 
          class="absolute" 
        />
      </div>
    `,
    methods: {
      close() {
        Dialog.close()
      }
    }
  }
}

// 弹窗配置
const dialogList = {
  weChat: {
    message: createDialogContent(`
      <div class="dialog-container dialog-container-weChat">
        <p>${t('customer.dialog.wechat.needSupport')}</p>
        <img
          class="my-2 w-[120px] h-[120px]"
          src="${getImageUrl('WechatQR.png')}"
          alt="WechatQR"
        />
        <p class="text-[#666] text-xs">
          ${t('customer.dialog.wechat.withWeChat')}
        </p>
      </div>
    `),
    confirmButtonText: t('customer.dialog.wechat.saveTheQR'),
    click: () => {
      saveImg('https://qn.lavamusic.com/qr/WechatQR.png')
    }
  },
  facebook: {
    message: createDialogContent(`
      <div class="flex flex-col items-center text-base">
        <p class="mb-2 font-medium">
          ${t('customer.dialog.facebook.needSupport')}
        </p>
        <p class="font-normal text-[#666] text-base">
          ${t('customer.dialog.facebook.FacebookToContact')}
        </p>
      </div>
    `),
    confirmButtonText: t('customer.dialog.facebook.copyAccount'),
    click: () => {
      dsBridge.call('copyToClipboard', { text: 'lavamusicofficail' }, (res) => {
        console.log('copyToClipboard', res)
      })
    }
  },
  whatsApp: {
    message: createDialogContent(`
      <div class="dialog-container dialog-container-whatsApp">
        <p class="text-base">LAVA Support</p>
        <p class="text-xs text-[#999]">WhatsApp contact</p>
        <img
          class="my-2 w-[120px] h-[120px]"
          src="${getImageUrl('WhatsAppQR.png')}"
          alt="WhatsAppQR"
        />
        <p class="text-[#666] text-xs">
          ${t('customer.dialog.whatsApp.needSupport')}
        </p>
      </div>
    `),
    confirmButtonText: t('customer.dialog.whatsApp.saveTheQR'),
    click: () => {
      saveImg('https://qn.lavamusic.com/qr/WhatsAppQR.png')
    }
  }
}

// 点击处理
const toApp = (item) => {
  const { url, name, analytics } = item
  const dialogItem = dialogList[name]

  // 埋点
  dsBridge.call('analytics', {
    event: 'customer_service_type',
    params: { CUSTOMER_SERVICE_TYPE: analytics }
  })

  // WhatsApp 不使用弹窗逻辑
  if (name !== 'whatsApp') {
    const time = setTimeout(() => {
      Dialog.alert({
        ...dialogItem,
        confirmButtonColor: '#000'
      }).then(() => {
        dialogItem.click()
      })
    }, 1500)

    // 清理定时器
    const clearTimer = () => clearTimeout(time)
    document.addEventListener('visibilitychange', clearTimer)
    window.addEventListener('hashchange', clearTimer)

    // 组件卸载时清理
    onUnmounted(() => {
      document.removeEventListener('visibilitychange', clearTimer)
      window.removeEventListener('hashchange', clearTimer)
    })
  }

  // 打开链接
  if (isIOS()) {
    dsBridge.call('openAction', { pageType: 5, url })
  } else {
    if (/LAVA/.test(navigator.userAgent) || region === 'zh') {
      dsBridge.call('openAction', { pageType: 5, url })
      return
    }
    window.location.href = url
  }
}

// 暴露响应式数据
const { list } = toRefs(state)
</script>

<style lang="scss" scoped>
.customer-service {
  padding: 10px 16px 0;
  background: #f7f7f7;
  min-height: 100vh;
}

.service-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  height: 72px;
  background: white;
  border-radius: 8px;
  margin-bottom: 16px;
  cursor: pointer;

  .item-content {
    display: flex;
    align-items: center;
    flex: 1;
  }

  .service-icon {
    @apply mr-2;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    margin-right: 8px;
  }

  .service-title {
    font-size: 16px;
    line-height: 24px;
  }

  .arrow-icon {
    width: 6.5px;
  }
}

.dialog-container {
  @apply flex flex-col items-center;
  display: flex;
  align-items: center;
  flex-direction: column;

  .qr-code {
    @apply my-2;
    width: 120px;
    height: 120px;
  }

  .qr-tip {
    @apply text-xs text-gray-600;
  }
}
</style>